import { useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
// Components should catch and toast errors; client parses and throws

const DEFAULT_API_BASE_URL = 'https://api-dev.mobie.at';

export function getApiBaseUrl(): string {
  const configuredBaseUrl =
    (process.env.REACT_APP_API_BASE_URL || (window as any).REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL).trim();

  return configuredBaseUrl.replace(/\/+$/, '');
}

export function buildApiUrl(input: string): string {
  if (/^https?:\/\//i.test(input)) {
    return input;
  }

  const normalizedPath = input.startsWith('/') ? input : `/${input}`;
  return `${getApiBaseUrl()}${normalizedPath}`;
}

export function useApiClient() {
  const { getToken, initialized } = useAuth();

  const authFetch = useCallback(
    async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
      // Wait for Auth0 to be initialized before making API calls
      if (!initialized) {
        throw new Error('Authentication not initialized');
      }
      const token = await getToken();
      const headers = new Headers(init?.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      const url = typeof input === 'string' ? buildApiUrl(input) : input;
      return fetch(url, { ...init, headers });
    },
    [getToken, initialized]
  );

  const request = useCallback(
    async <T,>(input: RequestInfo, init?: RequestInit): Promise<T> => {
      const headers = new Headers(init?.headers);
      headers.set('Accept', 'application/json');
      if (!(init && init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
      }

      const res = await authFetch(input, { ...init, headers });
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const json = await res.json();
            message = json?.message || json?.error || JSON.stringify(json);
          } else {
            const text = await res.text();
            message = text || message;
          }
        } catch (_) {
          // ignore parse errors
        }
        throw new Error(message);
      }
      if (res.status === 204) return undefined as unknown as T;
      return (await res.json()) as T;
    },
    [authFetch]
  );

  return { request, authFetch };
}

export type AdminAnalyticsDailyActivityDto = {
  date: string;
  newUsers: number;
  activeUsers: number;
  rideSearches: number;
  rideRequests: number;
  rides: number;
  completedBookings: number;
};

export type CommunicationMetricsDto = {
  from: string;
  to: string;
  funnel: Record<string, number>;
  pushOpenRate: number;
  emailDeliveryRate: number;
  humanEmailOpenRate: number;
  clickRate: number;
  conversionRate: number;
};

export type AdminCommunicationDeliveryDto = {
  id: string;
  attemptNumber: number;
  status: string;
  attemptedAt: string;
  providerMessageId?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
};

export type AdminCommunicationEventDto = {
  type: string;
  occurredAt: string;
  human?: boolean | null;
  providerEventId?: string | null;
  reason?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  severity?: string | null;
};

export type AdminCommunicationDto = {
  id: string;
  createdAt: string;
  scheduledAt: string;
  updatedAt: string;
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  recipientType?: "USER" | "LEAD";
  leadId?: string | null;
  journey: string;
  variant?: string | null;
  category: string;
  channel: string;
  provider: string;
  status: string;
  subject?: string | null;
  title?: string | null;
  body?: string | null;
  templateId?: string | null;
  suppressionReason?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  deliveries: AdminCommunicationDeliveryDto[];
  events: AdminCommunicationEventDto[];
};

export type CommunicationTestTemplateDto = {
  key: string;
  label: string;
  journey: string;
  channel: string;
  provider: string;
  subject?: string | null;
  title?: string | null;
  body?: string | null;
  configured: boolean;
};

export type CommunicationTestSendDto = { id: string; status: string };

export async function getCommunicationMetrics(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  days: number,
): Promise<CommunicationMetricsDto> {
  return await request(`/admin/communications/metrics?days=${days}`, { method: 'GET' });
}

export async function getAdminCommunications(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  query: { page: number; size: number; q?: string; journey?: string; status?: string; channel?: string; provider?: string },
): Promise<PageDto<AdminCommunicationDto>> {
  const params = new URLSearchParams({ page: String(query.page), size: String(query.size) });
  for (const key of ['q', 'journey', 'status', 'channel', 'provider'] as const) {
    if (query[key]?.trim()) params.set(key, query[key]!.trim());
  }
  return await request(`/admin/communications?${params.toString()}`, { method: 'GET' });
}

export async function getCommunicationTestTemplates(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
): Promise<CommunicationTestTemplateDto[]> {
  return await request('/admin/communications/test-templates', { method: 'GET' });
}

export async function cancelCommunication(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  communicationId: string,
): Promise<void> {
  await request(`/admin/communications/${communicationId}`, { method: 'DELETE' });
}

export async function sendCommunicationTest(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string,
  templateKey: string,
): Promise<CommunicationTestSendDto> {
  return await request('/admin/communications/test-send', {
    method: 'POST',
    body: JSON.stringify({ userId, templateKey }),
  });
}

export type AdminAnalyticsBreakdownDto = { key: string; users: number };

export type AdminAnalyticsCommunityDto = {
  communityId: string;
  communityName: string;
  currentMembers: number;
  membersAtPeriodStart: number;
  membersAtPeriodEnd: number;
  joinedUsers: number;
  leftUsers: number;
  netGrowth: number;
  growthRate?: number | null;
};

export type AdminAnalyticsRideActivityDto = {
  date: string;
  offeredRides: number;
  completedRides: number;
  completedPassengerTrips: number;
};

export type AdminAnalyticsDashboardDto = {
  from: string;
  to: string;
  timeZone: string;
  generatedAt: string;
  goals: {
    registeredUsers: number;
    activeRideSeries: number;
    groupRideSeries: number;
    nonGroupRideSeries: number;
    completedNonGroupRides: number;
    activeGroups: number;
    activeWindowFrom: string;
    activeWindowTo: string;
  };
  groups: {
    totalGroups: number;
    offeredRides: number;
    completedRides: number;
    completedPassengerTrips: number;
    activity: AdminAnalyticsRideActivityDto[];
    items: Array<{
      groupId: string;
      groupName: string;
      communityId: string;
      communityName: string;
      offeredRides: number;
      completedRides: number;
      completedPassengerTrips: number;
      completedRidesLast7Days: number;
      completedPassengerTripsLast7Days: number;
    }>;
  };
  overview: {
    totalUsers: number;
    newUsers: number;
    activeUsers: number;
    onboardingCompletedUsers: number;
    onboardingCompletionRate: number;
    rideSearches: number;
    completedBookings: number;
    usageDataSince?: string | null;
    newUsersWithPhone: number;
    phoneNumberCoverageRate: number;
    newUsersVerified: number;
    verificationRate: number;
    newUsersDiditVerified: number;
    diditVerificationRate: number;
    newUsersStripeVerified: number;
    stripeVerificationRate: number;
  };
  activity: AdminAnalyticsDailyActivityDto[];
  users: {
    funnel: Array<{ key: string; users: number }>;
    onboardingDropOffs: AdminAnalyticsBreakdownDto[];
    acquisitionSources: AdminAnalyticsBreakdownDto[];
    mobilityRoles: AdminAnalyticsBreakdownDto[];
    languages: AdminAnalyticsBreakdownDto[];
  };
  devicePlatforms?: {
    knownInstallations: number;
    iosInstallations: number;
    iosPercentage: number;
    androidInstallations: number;
    androidPercentage: number;
  };
  marketplace: {
    rideSearches: number;
    successfulSearches: number;
    zeroResultSearches: number;
    zeroResultRate: number;
    averageSearchResults: number;
    rideRequests: number;
    rides: number;
    bookings: number;
    completedBookings: number;
    cancelledBookings: number;
    completedRides: number;
    completedPassengerTrips: number;
    activity: AdminAnalyticsRideActivityDto[];
  };
  communities: {
    totalCommunities: number;
    currentMemberships: number;
    items: AdminAnalyticsCommunityDto[];
    dashboardVisits?: {
      totalVisits: number;
      uniqueManagers: number;
      daily: Array<{ date: string; visits: number }>;
      managers: Array<{
        date: string; managerId: string; managerName: string;
        communityId: string; communityName: string; visits: number;
        firstVisit: string; lastVisit: string;
      }>;
      recent: Array<{
        visitId: string; managerId: string; managerName: string;
        communityId: string; communityName: string; visitedAt: string;
      }>;
    };
  };
  guidedTour: {
    totalRuns: number;
    uniqueUsers: number;
    completedRuns: number;
    skippedRuns: number;
    abandonedRuns: number;
    inProgressRuns: number;
    completionRate: number;
    skipRate: number;
    outcomes: Array<{ key: string; runs: number }>;
    dropOffs: Array<{ key: string; runs: number }>;
    branches: Array<{ key: string; runs: number }>;
  };
};

export type AdminInterestAnalyticsDto = {
  generatedAt: string;
  summary: {
    totalUsers: number;
    usersWithInterests: number;
    usersWithoutInterests: number;
    participationRate: number;
    totalSelections: number;
    averagePerUser: number;
    averagePerParticipatingUser: number;
  };
  interests: Array<{
    interestId: string; name: string; nameEn?: string | null; nameDe?: string | null;
    categoryId: string; userCount: number;
  }>;
  categories: Array<{
    categoryId: string; name: string; nameEn?: string | null; nameDe?: string | null; userCount: number;
  }>;
  distribution: Array<{ interestCount: number; userCount: number }>;
};

export async function getAdminInterestAnalytics(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>
): Promise<AdminInterestAnalyticsDto> {
  return await request('/admin/analytics/interests', { method: 'GET' });
}

export async function getAdminAnalytics(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  query: { timeZone: string } & ({ from: string; to: string } | { allTime: true })
): Promise<AdminAnalyticsDashboardDto> {
  const params = new URLSearchParams({ timeZone: query.timeZone });
  if ('allTime' in query) {
    params.set('allTime', 'true');
  } else {
    params.set('from', query.from);
    params.set('to', query.to);
  }
  return await request(`/admin/analytics?${params.toString()}`, { method: 'GET' });
}

export type AdminConnectPayoutOverviewDto = {
  totalPaidRideBookings: number;
  capturedPaidRideBookings: number;
  captureFailedBookings: number;
  connectReadyDriverCount: number;
  incompleteOnboardingDriverCount: number;
  inReviewDriverCount: number;
  payoutsDisabledDriverCount: number;
  staleConnectStatusDriverCount: number;
  capturedWithoutConnectRoutingCount: number;
  expectedConnectEarningsInCents: number;
  blockedConnectEarningsInCents: number;
  serviceFeesEarnedInCents: number;
};

export type AdminConnectPayoutDriverDto = {
  userId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  stripeConnectedAccountId?: string | null;
  stripeConnectDetailsSubmitted: boolean;
  stripeConnectChargesEnabled: boolean;
  stripeConnectPayoutsEnabled: boolean;
  stripeConnectReviewPending: boolean;
  stripeConnectLastSyncedAt?: string | null;
  connectStatus: string;
  connectStatusStale: boolean;
  capturedPaidBookingCount: number;
  captureFailedBookingCount: number;
  expectedConnectEarningsInCents: number;
  blockedConnectEarningsInCents: number;
  issues: string[];
};

export type AdminPayoutDashboardDto = {
  overview: AdminConnectPayoutOverviewDto;
  drivers: AdminConnectPayoutDriverDto[];
};

export async function getAdminPayoutDashboard(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>
): Promise<AdminPayoutDashboardDto> {
  return await request('/admin/payouts', { method: 'GET' });
}

export async function refreshAdminDriverConnectStatus(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  driverId: string
): Promise<AdminPayoutDashboardDto> {
  return await request(`/admin/payouts/drivers/${encodeURIComponent(driverId)}/refresh-connect`, { method: 'POST' });
}

export async function createAdminDriverManualPayout(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  driverId: string
): Promise<AdminPayoutDashboardDto> {
  return await request(`/admin/payouts/drivers/${encodeURIComponent(driverId)}/manual-payout`, { method: 'POST' });
}

export type AdminPaymentOpsOverviewDto = {
  totalBookingCount: number;
  authorizedCount: number;
  capturePendingCount: number;
  capturedCount: number;
  cancelledCount: number;
  notRequiredCount: number;
  captureFailedCount: number;
  completedNotCapturedCount: number;
  capturedWithoutConnectRoutingCount: number;
  driverConnectIncompleteCount: number;
  connectStatusStaleCount: number;
  inactiveWithOpenIntentCount: number;
  stateMismatchCount: number;
  paymentIntentMissingCount: number;
};

export type AdminPaymentOpsBookingDto = {
  bookingId: string;
  rideId: string;
  rideRequestId?: string | null;
  rideStatus: AdminRideStatus;
  driver: AdminRidePersonDto;
  rider?: AdminRidePersonDto | null;
  departure: LocationSummaryDto;
  destination: LocationSummaryDto;
  rideTime?: string | null;
  bookingCreatedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  pickedUpAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  expiredAt?: string | null;
  priceInCents?: number | null;
  serviceFeeInCents?: number | null;
  driverEarningsInCents?: number | null;
  platformPriceEstimateInCents?: number | null;
  platformServiceFeeEstimateInCents?: number | null;
  compensationMode?: string | null;
  requiresPayment?: boolean | null;
  connectRoutingExpected?: boolean | null;
  connectRoutingReady?: boolean | null;
  connectStatusStale?: boolean | null;
  expectedDestinationAccountId?: string | null;
  expectedApplicationFeeInCents?: number | null;
  expectedDestinationAmountInCents?: number | null;
  stripePaymentIntentId?: string | null;
  paymentCaptureStatus?: string | null;
  paymentCaptureLastError?: string | null;
  paymentCaptureLastAttemptAt?: string | null;
  paymentCaptureRetryCount?: number | null;
  includedInPayout?: boolean | null;
  issues: string[];
};

export type PaymentMethodDto = {
  id: string;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
};

export async function getPaymentMethods(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>
): Promise<PaymentMethodDto[]> {
  return await request('/payments/methods', { method: 'GET' });
}

export async function getAdminPaymentOpsOverview(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>
): Promise<AdminPaymentOpsOverviewDto> {
  return await request('/admin/payment-ops/overview', { method: 'GET' });
}

export async function getAdminPaymentOpsBookings(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  query: { page: number; size: number; issue?: string; status?: string; q?: string; bookingId?: string }
): Promise<PageDto<AdminPaymentOpsBookingDto>> {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('size', String(query.size));
  if (query.issue) params.set('issue', query.issue);
  if (query.status) params.set('status', query.status);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.bookingId) params.set('bookingId', query.bookingId);
  return await request(`/admin/payment-ops/bookings?${params.toString()}`, { method: 'GET' });
}

export async function retryAdminPaymentCapture(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  bookingId: string
): Promise<AdminPaymentOpsBookingDto> {
  return await request(`/admin/payment-ops/bookings/${encodeURIComponent(bookingId)}/retry-capture`, { method: 'POST' });
}

export async function cancelAdminPaymentAuthorization(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  bookingId: string
): Promise<AdminPaymentOpsBookingDto> {
  return await request(`/admin/payment-ops/bookings/${encodeURIComponent(bookingId)}/cancel-authorization`, { method: 'POST' });
}

// Backend returns full Ride entities for /rides/me; define a minimal type reflecting what's used in the UI
export type RideSummaryDto = {
  id: string;
  numberOfSeats: number;
  time: string;
  createdAt: string;
  updatedAt: string;
  driverName: string;
  driverProfilePictureUrl: string;
  vehicleDisplayName: string;
  departure: LocationSummaryDto;
  destination: LocationSummaryDto;
  distance: number;
};

export async function getMyRides(request: (input: RequestInfo, init?: RequestInit) => Promise<any>): Promise<RideSummaryDto[]> {
  return await request('/rides/me', { method: 'GET' });
}

export type AdminRideStatus = 'SCHEDULED' | 'STARTED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
export type AdminRideSortBy = 'time' | 'createdAt';
export type AdminRideType = 'SINGLE' | 'RECURRING';

export type PageDto<T> = {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
};

export type WebsiteLeadCreateRequest = {
  name?: string | null;
  email: string;
  message?: string | null;
  postalCode?: string | null;
  phone?: string | null;
};

export type AdminWebsiteLeadDto = {
  id: string;
  createdAt: string;
  name?: string | null;
  email: string;
  message?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  registered: boolean;
  registeredUserId?: string | null;
  registeredUserEmail?: string | null;
  registeredUserName?: string | null;
  registeredUserDeleted: boolean;
};

export type WebsiteLeadImportResultDto = {
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
};

export type WebsiteLeadFilterQuery = {
  q?: string;
  postalCode?: string;
  userMatch?: string;
};

export async function createWebsiteSignup(input: WebsiteLeadCreateRequest): Promise<void> {
  const res = await fetch(buildApiUrl('/public/website-signups'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

export async function getAdminWebsiteLeads(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  query: WebsiteLeadFilterQuery & { page: number; size: number }
): Promise<PageDto<AdminWebsiteLeadDto>> {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('size', String(query.size));
  appendWebsiteLeadFilterParams(params, query);
  return await request(`/admin/leads?${params.toString()}`, { method: 'GET' });
}

export async function exportAdminWebsiteLeadsCsv(
  authFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>,
  query: WebsiteLeadFilterQuery
): Promise<{ fileName: string; blob: Blob }> {
  const params = new URLSearchParams();
  appendWebsiteLeadFilterParams(params, query);
  const url = `/admin/leads/export${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await authFetch(url, { method: 'GET', headers: { Accept: 'text/csv' } });
  if (!response.ok) {
    throw new Error(await readErrorResponse(response));
  }
  return {
    fileName: contentDispositionFileName(response.headers.get('content-disposition')) || 'website-leads.csv',
    blob: await response.blob(),
  };
}

function appendWebsiteLeadFilterParams(params: URLSearchParams, query: WebsiteLeadFilterQuery) {
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.postalCode?.trim()) params.set('postalCode', query.postalCode.trim());
  if (query.userMatch?.trim()) params.set('userMatch', query.userMatch.trim());
}

export async function importAdminWebsiteLeads(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  file: File
): Promise<WebsiteLeadImportResultDto> {
  const formData = new FormData();
  formData.set('file', file);
  return await request('/admin/leads/import', { method: 'POST', body: formData });
}

export type AdminRidePersonDto = {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

export type AdminRideReviewDto = {
  id?: string | null;
  reviewer?: AdminRidePersonDto | null;
  reviewee?: AdminRidePersonDto | null;
  starRating?: number | null;
  comment?: string | null;
  state: 'SUBMITTED' | 'DISCARDED' | 'PENDING' | string;
  createdAt?: string | null;
  updatedAt?: string | null;
  discardedAt?: string | null;
};

export type AdminRideChatMessageDto = {
  id: string;
  sender: AdminRidePersonDto;
  senderRole?: string | null;
  content: string;
  timestamp: string;
};

export type AdminRideChatSummaryDto = {
  messageCount: number;
  lastMessageAt?: string | null;
  recentMessages: AdminRideChatMessageDto[];
  hasMore: boolean;
};

export type AdminRideBookingDto = {
  id: string;
  rideRequestId?: string | null;
  rideRequestStatus?: string | null;
  rider?: AdminRidePersonDto | null;
  pickup?: LocationSummaryDto | null;
  dropoff?: LocationSummaryDto | null;
  seatsRequested?: number | null;
  priceInCents?: number | null;
  serviceFeeInCents?: number | null;
  stripePaymentIntentId?: string | null;
  paymentCaptureStatus?: string | null;
  paymentCaptureLastError?: string | null;
  paymentCaptureLastAttemptAt?: string | null;
  paymentCaptureRetryCount?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  pickedUpAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  expiredAt?: string | null;
  actualRouteCoordinates?: number[][];
  driverReviewOfRider?: AdminRideReviewDto | null;
  riderReviewOfDriver?: AdminRideReviewDto | null;
};

export type AdminRideSummaryDto = {
  id: string;
  status: AdminRideStatus;
  time: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  expiredAt?: string | null;
  driver: AdminRidePersonDto;
  vehicle?: {
    id: string;
    make?: string | null;
    model?: string | null;
    licensePlate?: string | null;
    color?: string | null;
  } | null;
  departure: LocationSummaryDto;
  destination: LocationSummaryDto;
  numberOfSeats: number;
  bookingCount: number;
  activePassengerCount: number;
  approvedSeats: number;
  totalPriceInCents: number;
  totalDriverEarningsInCents: number;
  paymentIssueCount: number;
  recurring: boolean;
  recurringSeriesId?: string | null;
  recurringSeriesName?: string | null;
};

export type AdminRideDetailsDto = AdminRideSummaryDto & {
  routeId: string;
  distanceMeters?: number | null;
  durationSeconds?: number | null;
  totalServiceFeeInCents: number;
  plannedRouteCoordinates: number[][];
  actualRouteCoordinates: number[][];
  bookings: AdminRideBookingDto[];
  chat: AdminRideChatSummaryDto;
};

export type AdminRideQuery = {
  page: number;
  size: number;
  status?: AdminRideStatus | '';
  q?: string;
  userId?: string;
  sortBy?: AdminRideSortBy;
  eventId?: string;
  rideType?: AdminRideType | '';
  communityId?: string;
  userRole?: 'DRIVER' | 'RIDER';
  from?: string;
  to?: string;
  sortDirection?: 'ASC' | 'DESC';
};

export async function getAdminRides(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  query: AdminRideQuery
): Promise<PageDto<AdminRideSummaryDto>> {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('size', String(query.size));
  if (query.status) params.set('status', query.status);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.userId?.trim()) params.set('userId', query.userId.trim());
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.eventId?.trim()) params.set('eventId', query.eventId.trim());
  if (query.rideType) params.set('rideType', query.rideType);
  if (query.communityId) params.set('communityId', query.communityId);
  if (query.userRole && query.userId) params.set('userRole', query.userRole);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.sortDirection) params.set('sortDirection', query.sortDirection);
  return await request(`/admin/rides?${params.toString()}`, { method: 'GET' });
}

export type AdminEventCategory = 'SPORTS' | 'CULTURE' | 'MUSIC' | 'FESTIVAL' | 'MEETING' | 'WORKSHOP' | 'OTHER';

export type AdminEventLocationDto = {
  id?: string;
  publicName: string;
  addressLine?: string | null;
  longitude: number;
  latitude: number;
  rawFeatureJson?: string | null;
};

export type AdminEventDto = {
  id: string;
  communityId: string;
  communityName: string;
  name: string;
  description: string;
  startTime: string;
  endTime?: string | null;
  location: AdminEventLocationDto;
  imageUrl?: string | null;
  category: AdminEventCategory;
  maxParticipants?: number | null;
  registrationRequired: boolean;
  registrationDeadline?: string | null;
  voucherEmailText?: string | null;
  voucherEnabled?: boolean | null;
  createdById: string;
  createdByName?: string | null;
  createdAt: string;
  driversGoingCount: number;
  ridersLookingCount: number;
};

export type CreateAdminEventPayload = {
  communityId: string;
  name: string;
  description: string;
  startTime: string;
  endTime?: string | null;
  location: {
    publicName: string;
    addressLine?: string | null;
    longitude: number;
    latitude: number;
    rawFeatureJson?: string | null;
  };
  category: AdminEventCategory;
  maxParticipants?: number | null;
  registrationRequired: boolean;
  registrationDeadline?: string | null;
  voucherEmailText?: string | null;
  voucherEnabled?: boolean | null;
  managerUserIds?: string[];
};

export type EventManagerDto = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
};

export type AdminEventQuery = {
  page: number;
  size: number;
  query?: string;
  communityId?: string;
  startsAfter?: string;
};

export async function getAdminEvents(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  query: AdminEventQuery
): Promise<PageDto<AdminEventDto>> {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('size', String(query.size));
  if (query.query?.trim()) params.set('query', query.query.trim());
  if (query.communityId?.trim()) params.set('communityId', query.communityId.trim());
  if (query.startsAfter?.trim()) params.set('startsAfter', query.startsAfter.trim());
  return await request(`/admin/events?${params.toString()}`, { method: 'GET' });
}

export async function createAdminEvent(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  payload: CreateAdminEventPayload
): Promise<AdminEventDto> {
  return await request('/admin/events', { method: 'POST', body: JSON.stringify(payload) });
}

export async function getAdminEventManagers(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  eventId: string
): Promise<EventManagerDto[]> {
  return await request(`/admin/events/${encodeURIComponent(eventId)}/managers`, { method: 'GET' });
}

export async function addAdminEventManagers(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  eventId: string,
  userIds: string[]
): Promise<EventManagerDto[]> {
  return await request(`/admin/events/${encodeURIComponent(eventId)}/managers`, {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  });
}

export async function removeAdminEventManager(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  eventId: string,
  userId: string
): Promise<EventManagerDto[]> {
  return await request(`/admin/events/${encodeURIComponent(eventId)}/managers/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

export async function getAdminRide(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  rideId: string
): Promise<AdminRideDetailsDto> {
  return await request(`/admin/rides/${encodeURIComponent(rideId)}`, { method: 'GET' });
}

export async function getAdminRideChat(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  rideId: string,
  query: { page: number; size: number; senderId?: string; q?: string }
): Promise<PageDto<AdminRideChatMessageDto>> {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('size', String(query.size));
  if (query.senderId?.trim()) params.set('senderId', query.senderId.trim());
  if (query.q?.trim()) params.set('q', query.q.trim());
  return await request(`/admin/rides/${encodeURIComponent(rideId)}/chat?${params.toString()}`, { method: 'GET' });
}

export async function adminRideAction(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  rideId: string,
  action: 'start' | 'cancel' | 'complete'
): Promise<AdminRideDetailsDto> {
  return await request(`/admin/rides/${encodeURIComponent(rideId)}/${action}`, { method: 'POST' });
}

export async function adminCancelRideBooking(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  rideId: string,
  bookingId: string
): Promise<AdminRideDetailsDto> {
  return await request(`/admin/rides/${encodeURIComponent(rideId)}/bookings/${encodeURIComponent(bookingId)}/cancel`, { method: 'POST' });
}

export type AdminUserBaseDto = {
  profilePictureUrl?: string | null;
  optionalEmailOptedOutAt?: string | null;
  optionalEmailOptOutSource?: string | null;
  optionalEmailOptOutBy?: string | null;
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  verificationStatus?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastSeenAt?: string | null;
  deletedAt?: string | null;
  anonymizedAt?: string | null;
  onboardingVersion?: number | null;
  onboardingCompletedAt?: string | null;
  onboardingAcquisitionSource?: 'EVENT' | 'COMMUNITY' | 'OTHER' | null;
  onboardingOtherSource?: string | null;
  onboardingEventId?: string | null;
  onboardingEventName?: string | null;
  onboardingCommunityId?: string | null;
  onboardingCommunityName?: string | null;
  onboardingMobilityRole?: 'DRIVER' | 'RIDER' | 'BOTH' | null;
  onboardingVerificationChoice?: 'STRIPE' | 'DIDIT' | 'LATER' | null;
  onboardingPlaceName?: string | null;
  onboardingRegionName?: string | null;
  onboardingCountryCode?: string | null;
};

export type AdminUserSummaryDto = AdminUserBaseDto & {
  onboardingPendingStep: 'COMPLETED' | 'NAME' | 'PROFILE' | 'TERMS' | 'SOURCE' | 'EVENT' | 'COMMUNITY' | 'INTERESTS' | 'USAGE' | 'RIDE_PLAN' | 'VEHICLE' | 'VERIFICATION_CHOICE' | 'VERIFICATION' | 'STRIPE' | 'FINISH';
  agbAccepted: boolean;
  hasBio: boolean;
  diditVerified: boolean;
  hasVehicle: boolean;
  stripeConnectStatus: StripeConnectAccountStatus;
  singleRideCount: number;
  recurringRideCount: number;
  rideRequestCount: number;
  completedRideCount: number;
  completedRideRequestCount: number;
  hasCreatedRides: boolean;
  hasCreatedRideRequests: boolean;
  hasCompletedRides: boolean;
  hasCompletedRideRequests: boolean;
};

export type StripeConnectAccountStatus =
  | 'NO_ACCOUNT'
  | 'ONBOARDING_REQUIRED'
  | 'IN_REVIEW'
  | 'READY'
  | 'PAYOUTS_DISABLED';

export type BooleanFilterValue = 'any' | 'true' | 'false';
export type AdminUserActivityFilter = 'any' | '24h' | '7d' | '30d' | '90d' | 'never';
export type AdminUserSortBy = 'CREATED_AT' | 'UPDATED_AT' | 'LAST_SEEN_AT' | 'EMAIL';
export type SortDirection = 'ASC' | 'DESC';

export type AdminUserFilterQuery = {
  registeredFrom?: string;
  registeredTo?: string;
  lastSeenBefore?: string;
  mobilityRole?: 'DRIVER' | 'RIDER' | 'BOTH';
  emailOptedOut?: BooleanFilterValue;
  q?: string;
  includeDeleted?: boolean;
  lastSeenSince?: string | null;
  neverSeen?: boolean;
  agbAccepted?: BooleanFilterValue;
  hasBio?: BooleanFilterValue;
  diditVerified?: BooleanFilterValue;
  hasVehicle?: BooleanFilterValue;
  stripeConnectStatus?: StripeConnectAccountStatus | 'any';
  hasCreatedRides?: BooleanFilterValue;
  hasCreatedRideRequests?: BooleanFilterValue;
  hasCompletedRides?: BooleanFilterValue;
  hasCompletedRideRequests?: BooleanFilterValue;
  communityId?: string;
  groupId?: string;
  hasSearches?: BooleanFilterValue;
  hasEmergencyContacts?: BooleanFilterValue;
  hasReceivedReviews?: BooleanFilterValue;
  hasGivenReviews?: BooleanFilterValue;
  minAverageRating?: number | null;
  sortBy?: AdminUserSortBy;
  sortDirection?: SortDirection;
};

export type Auth0UserSyncRowDto = {
  auth0UserId: string;
  backendUserId?: string | null;
  email?: string | null;
  name?: string | null;
  status: string;
  reason?: string | null;
  auth0CreatedAt?: string | null;
  auth0LastLogin?: string | null;
  auth0LoginsCount?: number | null;
};

export type Auth0UserSyncResponse = {
  dryRun: boolean;
  auth0UsersChecked: number;
  created: number;
  alreadyExisting: number;
  wouldCreate: number;
  skipped: number;
  rows: Auth0UserSyncRowDto[];
};

export type AdminUserVehicleDto = {
  id: string;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  licensePlate?: string | null;
  color?: string | null;
  seats?: number | null;
  excludeTolls?: boolean | null;
  isDefault?: boolean | null;
  deleted?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AdminUserRideRequestDto = {
  id: string;
  status: string;
  time: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  dismissedAt?: string | null;
  seatsRequested?: number | null;
  distanceKm?: number | null;
  departure: LocationSummaryDto;
  destination: LocationSummaryDto;
};

export type AdminUserBookingDto = {
  id: string;
  rideId: string;
  rideRequestId?: string | null;
  rideRequestStatus?: string | null;
  rideStatus: AdminRideStatus;
  driver: AdminRidePersonDto;
  departure: LocationSummaryDto;
  destination: LocationSummaryDto;
  pickup?: LocationSummaryDto | null;
  dropoff?: LocationSummaryDto | null;
  rideTime: string;
  priceInCents?: number | null;
  serviceFeeInCents?: number | null;
  stripePaymentIntentId?: string | null;
  paymentCaptureStatus?: string | null;
  paymentCaptureLastError?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  pickedUpAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  expiredAt?: string | null;
};

export type AdminUserPayoutSummaryDto = {
  hasIban: boolean;
  payoutEligible: boolean;
  accountHolderName?: string | null;
  ibanLast4?: string | null;
  verificationStatus?: string | null;
  profileCreatedAt?: string | null;
  profileUpdatedAt?: string | null;
  pendingAmountInCents: number;
  exportedAmountInCents: number;
  paidOutAmountInCents: number;
  lastPayoutDate?: string | null;
  lastPayoutAmountInCents?: number | null;
};

export type AdminUserPayoutDto = {
  id: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  amountInCents?: number | null;
  bookingCount?: number | null;
  status?: string | null;
  exportReference?: string | null;
  exportedAt?: string | null;
  submittedAt?: string | null;
  paidAt?: string | null;
  failureReason?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type UserTransactionHistoryItemDto = {
  id: string;
  type: string;
  direction: string;
  status: string;
  amountInCents: number;
  currency: string;
  occurredAt?: string | null;
  rideId?: string | null;
  bookingId?: string | null;
  payoutId?: string | null;
  counterpartyUserId?: string | null;
  description?: string | null;
};

export type AdminUserReviewDto = {
  id: string;
  reviewer: AdminRidePersonDto;
  reviewee: AdminRidePersonDto;
  bookingId: string;
  rideId: string;
  rideTime?: string | null;
  departure: LocationSummaryDto;
  destination: LocationSummaryDto;
  starRating?: number | null;
  comment?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  discardedAt?: string | null;
};

export type AdminUserInterestDto = {
  id: string;
  name?: string | null;
  nameEn?: string | null;
  nameDe?: string | null;
  nameCs?: string | null;
  type?: string | null;
  typeNameEn?: string | null;
  typeNameDe?: string | null;
  typeNameCs?: string | null;
};

export type AdminUserCommunityDto = {
  id: string;
  name: string;
  description?: string | null;
  type?: string | null;
  membershipId: string;
  joinedAt?: string | null;
  leftAt?: string | null;
};

export type AdminUserDetailsDto = AdminUserBaseDto & {
  externalAuthSubject?: string | null;
  description?: string | null;
  dateOfBirth?: string | null;
  preferredLanguage?: string | null;
  hasFcmToken?: boolean | null;
  stripeCustomerId?: string | null;
  driverDetourMotivation?: string | null;
  riderDetourMotivation?: string | null;
  averageRating?: number | null;
  receivedReviewCount?: number | null;
  givenReviewCount?: number | null;
  agbAcceptedAt?: string | null;
  onboardingEventStartTime?: string | null;
  onboardingEventLocationName?: string | null;
  onboardingEventCommunityName?: string | null;
  onboardingLocationCapturedAt?: string | null;
  verificationDocuments: Array<{ id: string; documentType?: string | null; status?: string | null; expirationDate?: string | null }>;
  interests: AdminUserInterestDto[];
  communities: AdminUserCommunityDto[];
  vehicles: AdminUserVehicleDto[];
  paymentMethods: PaymentMethodDto[];
  paymentMethodsError?: string | null;
  payout: AdminUserPayoutSummaryDto;
  usage: AdminUserUsageSummaryDto;
  emergencyContacts: EmergencyContactDto[];
  groups: AdminUserGroupDto[];
};

export type AdminUserUsageSummaryDto = {
  searchCount: number;
  successfulSearchCount: number;
  failedSearchCount: number;
  zeroResultSearchCount: number;
  averageResultCount?: number | null;
  appSessionCount: number;
  appSessionCountLast7Days: number;
  appSessionCountLast30Days: number;
  activeDayCount: number;
  lastAppSessionAt?: string | null;
  latestDevice?: {
    installationId?: string | null;
    platform?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    osVersion?: string | null;
    appVersion?: string | null;
    appBuildNumber?: string | null;
    emulator?: boolean | null;
    lastSeenAt: string;
  } | null;
  savedLocationCount: number;
  lastUsageAt?: string | null;
};

export type AdminUserUsageEventDto = {
  id: string;
  eventType: 'RIDE_SEARCH' | 'APP_SESSION' | 'PENDING_REVIEWS_FETCH';
  status: 'STARTED' | 'SUCCEEDED' | 'FAILED';
  occurredAt: string;
  completedAt?: string | null;
  departureName?: string | null;
  departureAddress?: string | null;
  destinationName?: string | null;
  destinationAddress?: string | null;
  requestedTime?: string | null;
  seatsRequested?: number | null;
  resultCount?: number | null;
  pendingReviewCount?: number | null;
  pendingRideCount?: number | null;
  pendingBookingCount?: number | null;
  failureType?: string | null;
};

export type EmergencyContactDto = {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AdminUserGroupDto = {
  id: string;
  name: string;
  communityId: string;
  communityName: string;
};

export async function getAdminUsers(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  query: AdminUserFilterQuery & {
    page: number;
    size: number;
  }
): Promise<PageDto<AdminUserSummaryDto>> {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('size', String(query.size));
  appendAdminUserFilterParams(params, query);
  return await request(`/admin/users?${params.toString()}`, { method: 'GET' });
}

export async function exportAdminUsersCsv(
  authFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>,
  query: AdminUserFilterQuery
): Promise<{ fileName: string; blob: Blob }> {
  const params = new URLSearchParams();
  appendAdminUserFilterParams(params, query);
  const url = `/admin/users/export${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await authFetch(url, { method: 'GET', headers: { Accept: 'text/csv' } });
  if (!response.ok) {
    throw new Error(await readErrorResponse(response));
  }
  return {
    fileName: contentDispositionFileName(response.headers.get('content-disposition')) || 'admin-users.csv',
    blob: await response.blob(),
  };
}

export async function syncAuth0Users(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  dryRun = false
): Promise<Auth0UserSyncResponse> {
  return await request(`/admin/users/sync-auth0?dryRun=${String(dryRun)}`, { method: 'POST' });
}

function appendAdminUserFilterParams(params: URLSearchParams, query: AdminUserFilterQuery) {
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.includeDeleted) params.set('includeDeleted', 'true');
  for (const key of ['registeredFrom', 'registeredTo', 'lastSeenBefore', 'mobilityRole'] as const) {
    if (query[key]) params.set(key, query[key]!);
  }
  if (query.emailOptedOut && query.emailOptedOut !== 'any') params.set('emailOptedOut', query.emailOptedOut);
  if (query.lastSeenSince) params.set('lastSeenSince', query.lastSeenSince);
  if (query.neverSeen != null) params.set('neverSeen', String(query.neverSeen));
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortDirection) params.set('sortDirection', query.sortDirection);
  const appendBooleanFilter = (key: string, value?: BooleanFilterValue) => {
    if (value && value !== 'any') params.set(key, value);
  };
  appendBooleanFilter('agbAccepted', query.agbAccepted);
  appendBooleanFilter('hasBio', query.hasBio);
  appendBooleanFilter('diditVerified', query.diditVerified);
  appendBooleanFilter('hasVehicle', query.hasVehicle);
  appendBooleanFilter('hasCreatedRides', query.hasCreatedRides);
  appendBooleanFilter('hasCreatedRideRequests', query.hasCreatedRideRequests);
  appendBooleanFilter('hasCompletedRides', query.hasCompletedRides);
  appendBooleanFilter('hasCompletedRideRequests', query.hasCompletedRideRequests);
  appendBooleanFilter('hasSearches', query.hasSearches);
  appendBooleanFilter('hasEmergencyContacts', query.hasEmergencyContacts);
  appendBooleanFilter('hasReceivedReviews', query.hasReceivedReviews);
  appendBooleanFilter('hasGivenReviews', query.hasGivenReviews);
  if (query.communityId?.trim()) params.set('communityId', query.communityId.trim());
  if (query.groupId?.trim()) params.set('groupId', query.groupId.trim());
  if (query.minAverageRating != null) params.set('minAverageRating', String(query.minAverageRating));
  if (query.stripeConnectStatus && query.stripeConnectStatus !== 'any') {
    params.set('stripeConnectStatus', query.stripeConnectStatus);
  }
}

async function readErrorResponse(response: Response) {
  let message = `HTTP ${response.status}`;
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const json = await response.json();
      message = json?.message || json?.error || JSON.stringify(json);
    } else {
      message = await response.text() || message;
    }
  } catch (_) {
    // keep status fallback
  }
  return message;
}

function contentDispositionFileName(value: string | null) {
  if (!value) return null;
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const quotedMatch = value.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];
  const bareMatch = value.match(/filename=([^;]+)/i);
  return bareMatch?.[1]?.trim() || null;
}

export async function getAdminUser(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string
): Promise<AdminUserDetailsDto> {
  return await request(`/admin/users/${encodeURIComponent(userId)}`, { method: 'GET' });
}

export async function getAdminUserUsageEvents(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string,
  query: { page: number; size: number; searchesOnly?: boolean }
): Promise<PageDto<AdminUserUsageEventDto>> {
  const params = new URLSearchParams();
  params.set('type', query.searchesOnly ? 'SEARCHES' : 'USAGE_EVENTS');
  params.set('page', String(query.page));
  params.set('size', String(query.size));
  return await request(`/admin/users/${encodeURIComponent(userId)}/records?${params.toString()}`, { method: 'GET' });
}

function adminUserPageParams(query: { page: number; size: number; [key: string]: any }) {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('size', String(query.size));
  Object.entries(query).forEach(([key, value]) => {
    if (key !== 'page' && key !== 'size' && value != null && String(value).trim()) {
      params.set(key, String(value));
    }
  });
  return params.toString();
}

export async function getAdminUserDriverRides(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string,
  query: { page: number; size: number }
): Promise<PageDto<AdminRideSummaryDto>> {
  return await request(`/admin/users/${encodeURIComponent(userId)}/records?type=DRIVER_RIDES&${adminUserPageParams(query)}`, { method: 'GET' });
}

export async function getAdminUserBookings(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string,
  query: { page: number; size: number }
): Promise<PageDto<AdminUserBookingDto>> {
  return await request(`/admin/users/${encodeURIComponent(userId)}/records?type=BOOKINGS&${adminUserPageParams(query)}`, { method: 'GET' });
}

export async function getAdminUserRideRequests(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string,
  query: { page: number; size: number }
): Promise<PageDto<AdminUserRideRequestDto>> {
  return await request(`/admin/users/${encodeURIComponent(userId)}/records?type=RIDE_REQUESTS&${adminUserPageParams(query)}`, { method: 'GET' });
}

export async function getAdminUserReviews(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string,
  query: { page: number; size: number; direction: 'received' | 'given' }
): Promise<PageDto<AdminUserReviewDto>> {
  return await request(`/admin/users/${encodeURIComponent(userId)}/records?type=REVIEWS&${adminUserPageParams(query)}`, { method: 'GET' });
}

export async function getAdminUserPayouts(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string,
  query: { page: number; size: number }
): Promise<PageDto<AdminUserPayoutDto>> {
  return await request(`/admin/users/${encodeURIComponent(userId)}/records?type=PAYOUTS&${adminUserPageParams(query)}`, { method: 'GET' });
}

export async function getAdminUserTransactions(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userId: string,
  query: { page: number; size: number }
): Promise<PageDto<UserTransactionHistoryItemDto>> {
  return await request(`/admin/users/${encodeURIComponent(userId)}/records?type=TRANSACTIONS&${adminUserPageParams(query)}`, { method: 'GET' });
}

export type RideDetailsDto = {
  id: string;
  numberOfSeats: number;
  time: string;
  createdAt: string;
  updatedAt: string;
  selectedRoute: {
    id: string;
    departureLocation: { id: string; publicName: string; addressLine?: string | null; longitude: number; latitude: number };
    destinationLocation: { id: string; publicName: string; addressLine?: string | null; longitude: number; latitude: number };
    distance: number;
  };
  requestedRoutes: Array<{ id: string; distance: number }>;
};

export async function getRideDetails(request: (input: RequestInfo, init?: RequestInit) => Promise<any>, id: string): Promise<RideDetailsDto> {
  return await request(`/rides/${encodeURIComponent(id)}`, { method: 'GET' });
}

export type RideRequestSummaryDto = {
  id: string;
  status: string;
  time: string;
  createdAt: string;
  updatedAt: string;
  departure: LocationSummaryDto;
  destination: LocationSummaryDto;
};

export async function getMyRideRequests(request: (input: RequestInfo, init?: RequestInit) => Promise<any>): Promise<RideRequestSummaryDto[]> {
  return await request('/ride-requests/me', { method: 'GET' });
}

export type LocationSummaryDto = {
  id: string;
  publicName: string;
  addressLine?: string | null;
  longitude: number;
  latitude: number;
};

export type AdminNotificationType = 'COMMUNITY' | 'EVENT' | 'ADMIN' | 'USER';
export type AdminNotificationUserGroup = 'ALL' | 'COMMUNITY' | 'EVENT' | 'USER' | 'USERS';

export type CreateAdminNotificationPayload = {
  title: string;
  body: string;
  type: AdminNotificationType;
  userGroup: AdminNotificationUserGroup;
  communityId?: string | null;
  eventId?: string | null;
  userId?: string | null;
  userIds?: string[] | null;
  screen?: string | null;
};

export type AdminNotificationDto = {
  id: string;
  title: string;
  body: string;
  type: AdminNotificationType;
  userGroup: AdminNotificationUserGroup;
  communityId?: string | null;
  eventId?: string | null;
  userId?: string | null;
  userIds?: string[] | null;
  screen?: string | null;
  creatorUserId: string;
  targetUserCount: number;
  openedUserCount: number;
  openRate: number;
  createdAt: string;
  updatedAt: string;
};

export async function createAdminNotification(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  payload: CreateAdminNotificationPayload
): Promise<AdminNotificationDto> {
  return await request('/admin/notifications', { method: 'POST', body: JSON.stringify(payload) });
}

export async function getAdminNotifications(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  page = 0,
  size = 10
): Promise<PageDto<AdminNotificationDto>> {
  return await request(`/admin/notifications?page=${page}&size=${size}`, { method: 'GET' });
}

export type AdminNotificationCommunityOption = {
  id: string;
  name: string;
};

export type AdminNotificationEventOption = {
  id: string;
  communityId: string;
  communityName?: string | null;
  name: string;
  startTime: string;
};

export async function getAdminNotificationCommunities(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>
): Promise<AdminNotificationCommunityOption[]> {
  return await request('/communities', { method: 'GET' });
}

export async function getAdminNotificationEvents(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  communityId?: string | null
): Promise<PageDto<AdminNotificationEventOption>> {
  const params = new URLSearchParams();
  params.set('page', '0');
  params.set('size', '50');
  params.set('startsAfter', new Date(0).toISOString());
  if (communityId) params.set('communityId', communityId);
  return await request(`/events/search?${params.toString()}`, { method: 'GET' });
}

// Location management
export type LocationDetailsDto = {
  id: string;
  isPrivate: boolean;
  isFavorite: boolean;
  privateName?: string | null;
  publicName: string;
  addressLine?: string | null;
  longitude: number;
  latitude: number;
  rawFeatureJson?: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function getMyLocations(request: (input: RequestInfo, init?: RequestInit) => Promise<any>): Promise<LocationDetailsDto[]> {
  return await request('/locations/me', { method: 'GET' });
}

export async function getMyFavoriteLocations(request: (input: RequestInfo, init?: RequestInit) => Promise<any>): Promise<LocationDetailsDto[]> {
  return await request('/locations/me/favorites', { method: 'GET' });
}

export async function getLocation(request: (input: RequestInfo, init?: RequestInit) => Promise<any>, id: string): Promise<LocationDetailsDto> {
  return await request(`/locations/${encodeURIComponent(id)}`, { method: 'GET' });
}

export type UpdateLocationRequest = {
  privateName?: string | null;
  publicName?: string;
  addressLine?: string | null;
  isPrivate?: boolean;
};

export async function updateLocation(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  id: string,
  payload: UpdateLocationRequest
): Promise<LocationDetailsDto> {
  return await request(`/locations/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function toggleLocationFavorite(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  id: string,
  isFavorite: boolean
): Promise<LocationDetailsDto> {
  return await request(`/locations/${encodeURIComponent(id)}/favorite`, {
    method: 'PUT',
    body: JSON.stringify({ isFavorite })
  });
}

export async function deleteLocation(request: (input: RequestInfo, init?: RequestInit) => Promise<any>, id: string): Promise<void> {
  return await request(`/locations/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type AgentChannel = 'CHAT' | 'CALL';
export type AgentSessionStatus = 'ACTIVE' | 'COMPLETED' | 'HANDOFF_REQUIRED' | 'CANCELLED';
export type AgentMessageRole = 'USER' | 'ASSISTANT';

export type AgentSessionDto = {
  id: string;
  userId: string;
  channel: AgentChannel;
  status: AgentSessionStatus;
  transcriptReference?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AgentChatMessageDto = {
  id: string;
  role: AgentMessageRole;
  content: string;
  createdAt: string;
};

export type AgentChatResponse = {
  session: AgentSessionDto;
  assistantMessage: AgentChatMessageDto;
  messages: AgentChatMessageDto[];
};

export async function createAgentSession(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  payload?: { channel?: AgentChannel; transcriptReference?: string | null }
): Promise<AgentSessionDto> {
  return await request('/agent/sessions', {
    method: 'POST',
    body: JSON.stringify(payload ?? { channel: 'CHAT' }),
  });
}

export async function getAgentMessages(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  sessionId: string
): Promise<AgentChatMessageDto[]> {
  return await request(`/agent/sessions/${encodeURIComponent(sessionId)}/messages`, { method: 'GET' });
}

export async function sendAgentMessage(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  sessionId: string,
  message: string
): Promise<AgentChatResponse> {
  return await request(`/agent/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export type AdminBookingPhase = 'WAITING_ACCEPTANCE' | 'WAITING_START' | 'WAITING_PICKUP' | 'WAITING_DROPOFF' | 'COMPLETED' | 'REJECTED' | 'CANCELLED' | 'EXPIRED' | 'REVIEW_REQUIRED';
export type BookingLifecycleAction = 'BOOKING_CREATED' | 'BOOKING_APPROVED' | 'BOOKING_REJECTED' | 'BOOKING_PICKED_UP' | 'BOOKING_DROPPED_OFF' | 'BOOKING_CANCELLED' | 'BOOKING_EXPIRED' | 'RIDE_STARTED' | 'RIDE_COMPLETED' | 'RIDE_CANCELLED' | 'RIDE_EXPIRED';
export type BookingActorRole = 'DRIVER' | 'RIDER' | 'GUARDIAN' | 'ADMIN' | 'SYSTEM' | 'UNKNOWN';
export type BookingEventSource = 'LIFECYCLE_API' | 'REQUEST_JOIN' | 'RIDE_OFFER' | 'ACTION_ITEM' | 'CHILD_TRANSPORT' | 'ADMIN' | 'RECURRING_SERIES' | 'SYSTEM_JOB' | 'HISTORICAL';
export type BookingEventEffect = 'DIRECT' | 'DERIVED' | 'BACKFILLED';
export type BookingEventReason = 'NONE' | 'PICKUP_IMPLIED_START' | 'ALL_PASSENGERS_DROPPED_OFF' | 'ADMIN_COMPLETION' | 'NO_START_EXPIRY' | 'CAPACITY_REJECTION' | 'REQUEST_WITHDRAWN' | 'SERIES_CHANGE' | 'CONFIRMATION_EXPIRED' | 'TRANSPORT_SKIPPED' | 'ACCOUNT_DELETED';
export type AdminBookingSummaryDto = {
  bookingId: string; rideId: string; rideRequestId: string;
  requester: AdminRidePersonDto | null; driver: AdminRidePersonDto | null;
  pickup: LocationSummaryDto | null; dropoff: LocationSummaryDto | null;
  rideTime: string; createdAt: string; phase: AdminBookingPhase; lastActivityAt: string;
  paymentCaptureStatus: 'NOT_REQUIRED' | 'AUTHORIZED' | 'CAPTURE_PENDING' | 'CAPTURED' | 'CAPTURE_FAILED' | 'CANCELLED';
};
export type AdminBookingOverviewDto = {
  totalBookings: number;
  waitingAcceptance: number; waitingStart: number; waitingPickup: number; waitingDropoff: number; reviewRequired: number;
};
export type AdminBookingEventDto = {
  id: string; rideId: string; bookingId: string | null; action: BookingLifecycleAction; occurredAt: string;
  actorUserId: string | null; actor: AdminRidePersonDto | null; actorRole: BookingActorRole;
  source: BookingEventSource; effect: BookingEventEffect; reason: BookingEventReason; historical: boolean;
};
export type AdminBookingDetailDto = { booking: AdminBookingSummaryDto; events: AdminBookingEventDto[] };
export type AdminBookingQuery = { page?: number; size?: number; from?: string; to?: string; q?: string; phase?: AdminBookingPhase; openOnly?: boolean };
function bookingQueryString(query: AdminBookingQuery): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.set(key, String(value));
  });
  return params.toString();
}
export async function getAdminBookings(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>, filters: AdminBookingQuery
): Promise<PageDto<AdminBookingSummaryDto>> {
  return request(`/admin/bookings?${bookingQueryString(filters)}`, { method: 'GET' });
}
export async function getAdminBookingOverview(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>, filters: AdminBookingQuery
): Promise<AdminBookingOverviewDto> {
  return request(`/admin/bookings/overview?${bookingQueryString(filters)}`, { method: 'GET' });
}
export async function getAdminBooking(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>, bookingId: string
): Promise<AdminBookingDetailDto> {
  return request(`/admin/bookings/${encodeURIComponent(bookingId)}`, { method: 'GET' });
}


export type AdminPushReachabilityWindow = {
  days: number;
  users: number;
  registeredUsers: number;
  iosUsers: number;
  androidUsers: number;
  unknownPlatformUsers: number;
};

export type AdminPushReachabilityDto = {
  generatedAt: string;
  timeZone: string;
  trackingSince: string | null;
  windows: AdminPushReachabilityWindow[];
  history: Array<{ date: string; capturedAt: string; metrics: AdminPushReachabilityWindow }>;
};

export async function getAdminPushReachability(
  request: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  query: { from: string; to: string } | { allTime: true }
): Promise<AdminPushReachabilityDto> {
  const params = new URLSearchParams('allTime' in query ? { allTime: 'true' } : query);
  return await request(`/admin/analytics/push-reachability?${params.toString()}`, { method: 'GET' });
}
